import React, { useState, useEffect } from 'react';
import { Bell, BellDot, Trash2, X, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface NotificationCenterProps {
  userType: 'landlord' | 'tenant' | 'admin';
  userId: string;
}

interface UnifiedNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  source: 'admin' | 'landlord' | 'system';
  is_read: boolean;
  created_at: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
}

const NotificationCenter = ({ userType, userId }: NotificationCenterProps) => {
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userType || !userId) return;

    fetchNotifications();
    
    const cleanup = setupRealtimeSubscriptions();
    
    return cleanup;
  }, [userType, userId]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const notifications: UnifiedNotification[] = [];

      // Fetch admin notifications
      const { data: adminData } = await supabase
        .from('admin_notifications')
        .select('*')
        .or(`recipient_type.eq.all,and(recipient_type.eq.${userType},recipient_id.eq.${userId}),and(recipient_type.eq.individual,recipient_id.eq.${userId})`)
        .order('created_at', { ascending: false });

      if (adminData) {
        const mappedAdminNotifications = adminData.map(notification => ({
          id: notification.id,
          title: notification.title,
          message: notification.message,
          type: notification.notification_type,
          source: 'admin' as const,
          is_read: notification.is_read || false,
          created_at: notification.created_at,
          priority: getPriorityFromType(notification.notification_type),
          category: getCategoryFromType(notification.notification_type)
        }));
        notifications.push(...mappedAdminNotifications);
      }

      // Fetch landlord notifications if user is landlord
      if (userType === 'landlord') {
        const { data: landlordData } = await supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false });

        if (landlordData) {
          const mappedLandlordNotifications = landlordData.map(notification => ({
            id: `landlord_${notification.id}`,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            source: 'landlord' as const,
            is_read: notification.is_read || false,
            created_at: notification.created_at,
            priority: getPriorityFromType(notification.type),
            category: getCategoryFromType(notification.type)
          }));
          notifications.push(...mappedLandlordNotifications);
        }
      }

      // Sort by date and priority
      const sortedNotifications = notifications.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        
        if (dateA === dateB) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return dateB - dateA;
      });

      setNotifications(sortedNotifications);
      setUnreadCount(sortedNotifications.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: "خطأ في تحميل الإشعارات",
        description: "حدث خطأ أثناء تحميل الإشعارات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Remove any existing channels first
    supabase.removeAllChannels();
    
    // Subscribe to admin notifications
    const adminChannel = supabase
      .channel(`unified-admin-notifications-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'admin_notifications'
      }, (payload) => {
        const newNotification = payload.new;
        const isForCurrentUser = 
          newNotification.recipient_type === 'all' ||
          (newNotification.recipient_type === userType && newNotification.recipient_id === userId) ||
          (newNotification.recipient_type === 'individual' && newNotification.recipient_id === userId);

        if (isForCurrentUser) {
          const mappedNotification: UnifiedNotification = {
            id: newNotification.id,
            title: newNotification.title,
            message: newNotification.message,
            type: newNotification.notification_type,
            source: 'admin',
            is_read: false,
            created_at: newNotification.created_at,
            priority: getPriorityFromType(newNotification.notification_type),
            category: getCategoryFromType(newNotification.notification_type)
          };

          setNotifications(prev => [mappedNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          toast({
            title: `🔔 ${newNotification.title}`,
            description: newNotification.message,
            duration: 5000,
          });
        }
      })
      .subscribe();

    // Subscribe to landlord notifications if user is landlord
    let landlordChannel: any = null;
    if (userType === 'landlord') {
      landlordChannel = supabase
        .channel(`unified-landlord-notifications-${userId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        }, (payload) => {
          const newNotification = payload.new;
          const mappedNotification: UnifiedNotification = {
            id: `landlord_${newNotification.id}`,
            title: newNotification.title,
            message: newNotification.message,
            type: newNotification.type,
            source: 'landlord',
            is_read: false,
            created_at: newNotification.created_at,
            priority: getPriorityFromType(newNotification.type),
            category: getCategoryFromType(newNotification.type)
          };

          setNotifications(prev => [mappedNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          toast({
            title: `🔔 ${newNotification.title}`,
            description: newNotification.message,
            duration: 5000,
          });
        })
        .subscribe();
    }

    return () => {
      // Clean up channels properly
      try {
        supabase.removeChannel(adminChannel);
        if (landlordChannel) {
          supabase.removeChannel(landlordChannel);
        }
      } catch (error) {
        console.warn('Error removing channels:', error);
      }
    };
  };

  const getPriorityFromType = (type: string): 'low' | 'medium' | 'high' => {
    switch (type) {
      case 'rental_request':
      case 'subscription_expired':
      case 'account_activation':
        return 'high';
      case 'subscription_reminder':
      case 'account_status':
        return 'medium';
      default:
        return 'low';
    }
  };

  const getCategoryFromType = (type: string): string => {
    switch (type) {
      case 'rental_request':
        return 'طلبات الإيجار';
      case 'subscription_reminder':
      case 'subscription_expired':
        return 'الاشتراكات';
      case 'account_activation':
      case 'account_status':
        return 'الحساب';
      case 'admin_message':
        return 'رسائل إدارية';
      default:
        return 'عام';
    }
  };

  const markAsRead = async (notificationId: string, source: string) => {
    try {
      if (source === 'admin') {
        await supabase
          .from('admin_notifications')
          .update({ is_read: true })
          .eq('id', notificationId);
      } else if (source === 'landlord' && notificationId.startsWith('landlord_')) {
        const originalId = notificationId.replace('landlord_', '');
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', originalId);
      }

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string, source: string) => {
    try {
      let deleteSuccess = false;
      
      if (source === 'admin') {
        const { error } = await supabase
          .from('admin_notifications')
          .delete()
          .eq('id', notificationId);
        deleteSuccess = !error;
        if (error) console.error('Error deleting admin notification:', error);
        
      } else if (source === 'landlord' && notificationId.startsWith('landlord_')) {
        const originalId = notificationId.replace('landlord_', '');
        const { error } = await supabase
          .from('notifications')
          .delete()
          .eq('id', originalId);
        deleteSuccess = !error;
        if (error) console.error('Error deleting landlord notification:', error);
      }

      if (deleteSuccess) {
        // Update local state immediately
        const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }

        toast({
          title: "تم الحذف",
          description: "تم حذف الإشعار بنجاح"
        });
      } else {
        throw new Error('Failed to delete notification');
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: "خطأ في الحذف",
        description: "حدث خطأ أثناء حذف الإشعار",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return `منذ ${diffInMinutes} دقيقة`;
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else {
      return new Intl.DateTimeFormat('ar', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const filteredNotifications = activeTab === 'unread' 
    ? notifications.filter(n => !n.is_read)
    : notifications;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative hover:bg-muted/50 transition-colors"
        >
          {unreadCount > 0 ? (
            <BellDot className="h-5 w-5 text-primary" />
          ) : (
            <Bell className="h-5 w-5 text-muted-foreground" />
          )}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent side="left" className="w-full sm:w-[400px] md:w-96 p-0 max-w-full">
        <div className="flex flex-col h-full">
          <SheetHeader className="p-6 border-b bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-xl font-bold">الإشعارات</SheetTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="w-fit">
                {unreadCount} إشعار جديد
              </Badge>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <div className="px-6 pt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="all" className="text-sm">
                    جميع الإشعارات
                  </TabsTrigger>
                  <TabsTrigger value="unread" className="text-sm relative">
                    غير مقروءة
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="ml-2 h-4 w-4 p-0 text-xs">
                        {unreadCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="flex-1 m-0">
                <NotificationList 
                  notifications={filteredNotifications}
                  loading={loading}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  formatDate={formatDate}
                  getPriorityColor={getPriorityColor}
                />
              </TabsContent>

              <TabsContent value="unread" className="flex-1 m-0">
                <NotificationList 
                  notifications={filteredNotifications}
                  loading={loading}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  formatDate={formatDate}
                  getPriorityColor={getPriorityColor}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface NotificationListProps {
  notifications: UnifiedNotification[];
  loading: boolean;
  onMarkAsRead: (id: string, source: string) => void;
  onDelete: (id: string, source: string) => void;
  formatDate: (date: string) => string;
  getPriorityColor: (priority: string) => string;
}

const NotificationList = ({ 
  notifications, 
  loading, 
  onMarkAsRead, 
  onDelete, 
  formatDate, 
  getPriorityColor 
}: NotificationListProps) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
            <Bell className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
          <p className="text-sm text-muted-foreground">لا توجد إشعارات</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      <div className="space-y-2 p-4">
        {notifications.map((notification) => (
          <Card 
            key={notification.id} 
            className={`transition-all hover:shadow-md cursor-pointer ${
              !notification.is_read ? 'border-l-4 border-l-primary bg-primary/5' : ''
            }`}
            onClick={() => !notification.is_read && onMarkAsRead(notification.id, notification.source)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-sm line-clamp-1">
                      {notification.title}
                    </h4>
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                    {notification.message}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getPriorityColor(notification.priority)}`}
                      >
                        {notification.category}
                      </Badge>
                      <span className="text-muted-foreground">
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(notification.id, notification.source);
                      }}
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default NotificationCenter;